import React, { useEffect, useState } from 'react';
import { ScrollText, RefreshCw, Search } from 'lucide-react';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import { Button } from '../../components/ui/Button';
import api from '../../services/api';
import { cn } from '../../utils/cn';

const LEVEL_BADGE = {
  info: 'sf-badge-neutral',
  debug: 'sf-badge-neutral',
  warn: 'sf-badge-warning',
  error: 'sf-badge-error',
};

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'campaign', label: 'Bulk Campaigns' },
  { value: 'messaging', label: 'Messaging' },
  { value: 'automation', label: 'Automation' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'templates', label: 'Templates' },
  { value: 'flows', label: 'Flows' },
  { value: 'media', label: 'Media' },
  { value: 'api', label: 'API' },
  { value: 'system', label: 'System' },
];

export default function MetaLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState('campaign');
  const [level, setLevel] = useState('');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/meta/logs', {
        params: {
          category: category || undefined,
          level: level || undefined,
          search: search || undefined,
          limit: 200,
        },
      });
      setLogs(res.data);
    } catch (err) {
      console.error('Failed to fetch logs', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [category, level]);

  return (
    <DashboardModuleShell
      title="Message Logs"
      subtitle="Every WhatsApp send/receive attempt, automation run, and bulk campaign job — with the real error when something breaks."
      icon={ScrollText}
      actions={
        <Button variant="secondary" onClick={fetchLogs} loading={loading}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      }
    >
      <div className="sf-card mb-4">
        <div className="sf-card-body flex flex-wrap items-center gap-3">
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="h-9 px-3 bg-[#F4F6F9] border border-[#D8DDE6] rounded-md text-[12px]"
          >
            {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select
            value={level}
            onChange={e => setLevel(e.target.value)}
            className="h-9 px-3 bg-[#F4F6F9] border border-[#D8DDE6] rounded-md text-[12px]"
          >
            <option value="">All Levels</option>
            <option value="error">Error</option>
            <option value="warn">Warning</option>
            <option value="info">Info</option>
            <option value="debug">Debug</option>
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#706E6B]" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchLogs()}
              placeholder="Search message text…"
              className="w-full h-9 pl-9 pr-3 bg-[#F4F6F9] border border-[#D8DDE6] rounded-md text-[12px] outline-none focus:bg-white focus:border-[#0176D3]"
            />
          </div>
          <Button variant="secondary" size="sm" onClick={fetchLogs}>Search</Button>
        </div>
      </div>

      <div className="sf-card">
        {loading ? (
          <div className="text-center py-16 text-[13px] text-[#706E6B]">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 px-6">
            <ScrollText className="w-10 h-10 text-[#D8DDE6] mx-auto mb-4" />
            <h3 className="text-[15px] font-bold text-[#080707] mb-2">No logs match these filters</h3>
            <p className="text-[13px] text-[#706E6B]">Try a different category or clear the search.</p>
          </div>
        ) : (
          <div className="divide-y divide-[#D8DDE6]">
            {logs.map(log => (
              <div key={log._id} className="p-4 cursor-pointer hover:bg-[#F4F6F9]" onClick={() => setExpandedId(expandedId === log._id ? null : log._id)}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <span className={cn('sf-badge shrink-0', LEVEL_BADGE[log.level] || 'sf-badge-neutral')}>{log.level}</span>
                    <span className="sf-badge sf-badge-neutral shrink-0">{log.category}</span>
                    <p className="text-[12px] text-[#080707] break-words">{log.message}</p>
                  </div>
                  <span className="text-[11px] text-[#706E6B] shrink-0">{new Date(log.timestamp).toLocaleString()}</span>
                </div>
                {expandedId === log._id && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {log.details && (
                      <div>
                        <p className="text-[10px] font-bold text-[#706E6B] uppercase mb-1">Details</p>
                        <pre className="text-[11px] font-mono bg-[#0B0E14] text-[#E4E7EB] rounded-[4px] p-3 max-h-64 overflow-auto">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </div>
                    )}
                    {log.response?.body && (
                      <div>
                        <p className="text-[10px] font-bold text-[#706E6B] uppercase mb-1">Response</p>
                        <pre className="text-[11px] font-mono bg-[#0B0E14] text-[#E4E7EB] rounded-[4px] p-3 max-h-64 overflow-auto">
                          {JSON.stringify(log.response.body, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardModuleShell>
  );
}
